/**
 * CHI TIẾT NHIỆM VỤ — the start → complete flow.
 *
 * The action rail at the bottom offers exactly the transitions the SERVER says
 * are legal (`allowedEvents`, computed by the API from `taskMachine`). The app
 * never guesses: showing a "Bắt đầu" button on a task the state machine will
 * refuse is how an employee learns to distrust the app.
 *
 * EVIDENCE: capture → compress → `POST /files` → the returned `fileId` rides
 * the completion body as `evidenceFileId`. Upload progress, retry and a
 * server-side preview live in `EvidenceUploader`. A completion queued OFFLINE
 * still goes without a photo (uploads need the network), and the KPI
 * consequence (`MISSING_EVIDENCE`) is explained in place, not sprung later.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTasks, errorMessage } from '../lib/queries';
import { useServerClock, useSyncState } from '../lib/hooks';
import { kpiNotice, useCompleteTask, useStartTask } from '../lib/task-actions';
import { formatClock, formatTime } from '../lib/format';
import { TASK_STATUS_LABEL, TASK_STATUS_TONE, taskTypeLabel, urgencyLabel, urgencyTone } from '../lib/labels';
import type { TaskCompleteRequest } from '../lib/contracts';
import {
  Banner,
  Button,
  Card,
  ErrorState,
  KeyValue,
  LoadingState,
  Sheet,
  StatusBadge,
} from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { EvidenceUploader } from '../components/EvidenceUploader';
import { useToast } from '../components/Toast';

export function TaskDetailScreen(): JSX.Element {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useTasks();
  const now = useServerClock(data?.serverTime, 1000);
  const { online } = useSyncState();

  const start = useStartTask();
  const [completeOpen, setCompleteOpen] = useState(false);

  const task = useMemo(
    () => data?.accepted.find((t) => t.id === taskId) ?? data?.offered.find((t) => t.id === taskId) ?? null,
    [data, taskId],
  );

  if (isLoading) return <LoadingState />;
  if (isError || data === undefined) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }
  if (task === null) {
    return (
      <div>
        <ScreenHeader title="Nhiệm vụ" backTo="/cong-viec" />
        <ErrorState
          title="Không tìm thấy nhiệm vụ"
          message="Nhiệm vụ này không còn trong danh sách của bạn — có thể đã hoàn thành, đã hết hạn hoặc đã chuyển cho người khác."
          onRetry={() => navigate('/cong-viec')}
        />
      </div>
    );
  }

  const canStart = task.allowedEvents.includes('START');
  const canComplete = task.allowedEvents.includes('COMPLETE');
  const elapsedSeconds = task.startedAt === null ? null : Math.max(0, Math.floor((now - task.startedAt) / 1000));
  const overdue = task.dueAt !== null && now > task.dueAt;

  return (
    <div className="pb-44">
      <ScreenHeader title={task.title} subtitle={taskTypeLabel(task.type)} backTo="/cong-viec" />

      <Card>
        <div className="flex items-center justify-between gap-2">
          <StatusBadge tone={TASK_STATUS_TONE[task.status]}>{TASK_STATUS_LABEL[task.status]}</StatusBadge>
          <StatusBadge tone={urgencyTone(task.urgency)}>{urgencyLabel(task.urgency)}</StatusBadge>
        </div>

        {task.detail === null ? null : <p className="mt-3 text-base text-slate-800">{task.detail}</p>}

        <div className="mt-3 divide-y divide-slate-100 border-t border-slate-100 pt-1">
          <KeyValue label="Hạn hoàn thành" value={<span className={overdue ? 'text-rose-700' : ''}>{formatTime(task.dueAt)}</span>} />
          <KeyValue
            label="Định mức thời gian"
            value={task.expectedDurationSeconds === null ? '—' : formatClock(task.expectedDurationSeconds)}
          />
          {elapsedSeconds === null ? null : (
            <KeyValue label="Đã làm" value={<span className="tabular-nums">{formatClock(elapsedSeconds)}</span>} />
          )}
          <KeyValue label="Bàn / Trạm" value={task.tableId ?? task.stationId ?? '—'} />
          <KeyValue label="Đơn hàng" value={task.orderId ?? '—'} />
        </div>
      </Card>

      {overdue ? (
        <Banner tone="danger" title="Nhiệm vụ đã quá hạn">
          Hoàn thành ngay khi có thể. Việc trễ hạn được ghi nhận trong KPI, nhưng vẫn tốt hơn là bỏ dở.
        </Banner>
      ) : null}

      <p className="mt-4 rounded-2xl bg-slate-100 p-3 text-xs leading-relaxed text-slate-600">
        Thời gian xử lý được tính bằng <strong>giờ máy chủ</strong>, từ lúc bạn bấm “Bắt đầu” đến lúc bấm “Hoàn
        thành”. Giờ trên điện thoại không ảnh hưởng tới con số này.
      </p>

      {/* Actions in the bottom third, derived from the server's allowedEvents. */}
      <div className="action-dock">
        {canStart ? (
          <Button
            size="lg"
            fullWidth
            loading={start.isPending}
            disabled={!online}
            onClick={() =>
              start.mutate(task.id, {
                onSuccess: (result) => {
                  const notice = kpiNotice(result.kpiCounted, result.kpiRejectReason);
                  toast.success('Đã bắt đầu làm', notice ?? undefined);
                },
                onError: (e) => toast.error('Không bắt đầu được', errorMessage(e)),
              })
            }
          >
            Bắt đầu làm
          </Button>
        ) : canComplete ? (
          <Button variant="success" size="lg" fullWidth onClick={() => setCompleteOpen(true)}>
            Hoàn thành nhiệm vụ
          </Button>
        ) : (
          <Button size="lg" fullWidth disabled ariaLabel="Không có thao tác hợp lệ ở trạng thái hiện tại">
            Không có thao tác ở trạng thái này
          </Button>
        )}
      </div>

      <CompleteSheet
        open={completeOpen}
        taskId={task.id}
        taskTitle={task.title}
        onClose={() => setCompleteOpen(false)}
        onDone={() => {
          setCompleteOpen(false);
          navigate('/cong-viec');
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completion sheet with evidence capture
// ---------------------------------------------------------------------------

function CompleteSheet({
  open,
  taskId,
  taskTitle,
  onClose,
  onDone,
}: {
  readonly open: boolean;
  readonly taskId: string;
  readonly taskTitle: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const complete = useCompleteTask();
  const [result, setResult] = useState<'SUCCESS' | 'PARTIAL'>('SUCCESS');
  const [note, setNote] = useState('');
  const [evidenceFileId, setEvidenceFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const submit = (): void => {
    const body: TaskCompleteRequest = {
      result,
      note: note.trim() === '' ? null : note.trim(),
      // The REAL file id from POST /files — the KPI evidence gate reads this.
      evidenceFileId,
    };
    complete.mutate(
      { taskId, taskTitle, body },
      {
        onSuccess: (outcome) => {
          if (outcome.queued) {
            toast.warning(
              'Đã lưu, chờ gửi khi có mạng',
              'Giờ ghi nhận sẽ là lúc máy chủ nhận được, không phải lúc bạn bấm.',
            );
          } else {
            const notice = kpiNotice(outcome.response.kpiCounted, outcome.response.kpiRejectReason);
            toast.success(
              outcome.response.onTime ? 'Hoàn thành đúng hạn' : 'Đã hoàn thành (trễ hạn)',
              notice ?? `Thời gian xử lý: ${formatClock(outcome.response.durationSeconds)}`,
            );
          }
          setNote('');
          setEvidenceFileId(null);
          onDone();
        },
        onError: (e) => toast.error('Không hoàn thành được', errorMessage(e)),
      },
    );
  };

  return (
    <Sheet
      open={open}
      title="Hoàn thành nhiệm vụ"
      onClose={onClose}
      footer={
        <Button
          variant="success"
          size="lg"
          fullWidth
          loading={complete.isPending}
          // A tap mid-upload would complete WITHOUT the photo and trip the
          // MISSING_EVIDENCE gate — hold the button until the upload settles.
          disabled={uploading}
          onClick={submit}
        >
          {uploading ? 'Đang tải ảnh…' : 'Xác nhận hoàn thành'}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">{taskTitle}</p>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setResult('SUCCESS')}
          className={`min-h-touch-lg rounded-2xl border px-3 font-semibold ${result === 'SUCCESS' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200'}`}
        >
          Xong hoàn toàn
        </button>
        <button
          type="button"
          onClick={() => setResult('PARTIAL')}
          className={`min-h-touch-lg rounded-2xl border px-3 font-semibold ${result === 'PARTIAL' ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-200'}`}
        >
          Xong một phần
        </button>
      </div>

      <label className="label mt-4" htmlFor="complete-note">
        Ghi chú (không bắt buộc)
      </label>
      <textarea
        id="complete-note"
        className="field mt-1"
        rows={3}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Ví dụ: khách đổi bàn, thiếu 1 phần nước chấm…"
      />

      {/* --- evidence capture ------------------------------------------- */}
      <div className="mt-4">
        <EvidenceUploader
          label="Ảnh bằng chứng"
          hint="Với nhiệm vụ bắt buộc có bằng chứng, hoàn thành KHÔNG kèm ảnh vẫn được ghi nhận nhưng không tính điểm (MISSING_EVIDENCE)."
          purpose="EVIDENCE"
          onChange={setEvidenceFileId}
          onBusyChange={setUploading}
        />
      </div>
    </Sheet>
  );
}
